import { useEffect, useRef, useState } from 'react';
import {
  configApiRef,
  featureFlagsApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  detectErrors,
  KUBERNETES_ANNOTATION,
} from '@backstage/plugin-kubernetes-common';
import type { DetectedError, KubernetesRequestBody } from '@backstage/plugin-kubernetes-common';
import {
  kubernetesApiRef,
  kubernetesAuthProvidersApiRef,
} from '@backstage/plugin-kubernetes-react';

const POLL_INTERVAL_MS = 60_000;
const MAX_ALERTS = 5;
const DEFAULT_RESTART_THRESHOLD = 3;

// The kubernetes feature flag gates the Kubernetes tab in EntityPage.
// The banner must respect the same flag so clicking through always lands on a visible tab.
const KUBERNETES_FEATURE_FLAG = 'kubernetes';

const TITLE_MAP: Record<string, string> = {
  CrashLoopBackOff: 'Pod in CrashLoopBackOff',
  OOMKilled: 'Pod OOMKilled',
  ImagePullBackOff: 'Image pull failure',
  ErrImagePull: 'Image pull failure',
  CreateContainerConfigError: 'Container config error',
  ReadinessProbeFailed: 'Readiness probe failing',
  'readiness-probe-taking-too-long': 'Readiness probe failing',
  'containers-restarting': 'Pod restarts above threshold',
  'container-waiting': 'Pod not starting',
};

// Lower index = shown first. Mirrors operational impact: crash > OOM > image > config > readiness > restarts.
const SEVERITY_ORDER: Record<string, number> = {
  CrashLoopBackOff: 0,
  OOMKilled: 1,
  ImagePullBackOff: 2,
  ErrImagePull: 2,
  CreateContainerConfigError: 3,
  ReadinessProbeFailed: 4,
  'readiness-probe-taking-too-long': 4,
  'container-waiting': 3,
  'containers-restarting': 5,
};

export interface KubernetesAlert {
  /** Stable deduplication key: "{entityRef}:{podName}:{clusterName}:{errorType}" */
  id: string;
  entityRef: string;
  entityName: string;
  podName: string;
  cluster: string;
  title: string;
  detail: string;
  /** Path to the entity's Kubernetes tab */
  kubernetesPath: string;
  occurrenceCount: number;
}

export interface UseKubernetesAlertsResult {
  alerts: KubernetesAlert[];
  overflowCount: number;
}

interface RawAlert {
  alert: KubernetesAlert;
  severityRank: number;
}

function getAlertTitle(err: DetectedError): string {
  if (err.type === 'containers-restarting') return 'Pod restarts above threshold';
  const key = err.proposedFix?.errorType ?? err.type;
  return TITLE_MAP[key] ?? 'Kubernetes issue';
}

function getSeverityRank(err: DetectedError): number {
  if (err.type === 'containers-restarting') return SEVERITY_ORDER['containers-restarting'] ?? 5;
  const key = err.proposedFix?.errorType ?? err.type;
  return SEVERITY_ORDER[key] ?? 99;
}

export function useKubernetesAlerts(): UseKubernetesAlertsResult {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const kubernetesApi = useApi(kubernetesApiRef);
  const kubernetesAuthProvidersApi = useApi(kubernetesAuthProvidersApiRef);
  const configApi = useApi(configApiRef);
  const featureFlagsApi = useApi(featureFlagsApiRef);

  const [state, setState] = useState<UseKubernetesAlertsResult>({
    alerts: [],
    overflowCount: 0,
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const poll = async () => {
      try {
        // Respect the same feature flag that gates the Kubernetes tab in EntityPage.
        // Without this, clicking the banner can land on a page where the tab doesn't exist.
        if (!featureFlagsApi.isActive(KUBERNETES_FEATURE_FLAG)) {
          if (mountedRef.current) setState({ alerts: [], overflowCount: 0 });
          return;
        }

        const restartThreshold =
          configApi.getOptionalNumber('myDay.kubernetesAlerts.restartThreshold') ??
          DEFAULT_RESTART_THRESHOLD;

        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;

        const groupsResponse = await catalogApi.getEntities({
          filter: { kind: 'Group', 'relations.hasMember': userEntityRef },
          fields: ['kind', 'metadata.namespace', 'metadata.name'],
        });

        if (groupsResponse.items.length === 0) {
          if (mountedRef.current) setState({ alerts: [], overflowCount: 0 });
          return;
        }

        const groupRefs = groupsResponse.items.map(g => stringifyEntityRef(g));

        const componentsResponse = await catalogApi.getEntities({
          filter: { kind: 'Component', 'relations.ownedBy': groupRefs },
          fields: ['kind', 'metadata.name', 'metadata.namespace', 'metadata.annotations'],
        });

        const k8sEntities = componentsResponse.items.filter(
          e => e.metadata.annotations?.[KUBERNETES_ANNOTATION] !== undefined,
        );

        if (k8sEntities.length === 0) {
          if (mountedRef.current) setState({ alerts: [], overflowCount: 0 });
          return;
        }

        // Build auth once per poll cycle, accumulating tokens for each unique auth provider.
        const clusters = await kubernetesApi.getClusters();
        const seenProviders = new Set<string>();
        let requestBody: KubernetesRequestBody = {
          entity: k8sEntities[0],
          auth: {},
        };
        for (const cluster of clusters) {
          if (!seenProviders.has(cluster.authProvider)) {
            seenProviders.add(cluster.authProvider);
            requestBody = await kubernetesAuthProvidersApi.decorateRequestBodyForAuth(
              cluster.authProvider,
              requestBody,
            );
          }
        }

        // Fetch all entities in parallel — auth is already resolved so this is safe.
        // Promise.allSettled lets a single failing entity not block the rest.
        const entityResponses = await Promise.allSettled(
          k8sEntities.map(entity =>
            kubernetesApi
              .getObjectsByEntity({ entity, auth: requestBody.auth })
              .then(response => ({ entity, response })),
          ),
        );

        // If every fetch failed, preserve the previous state (fail-silent).
        // Partial failures are fine — we show results from the successful entities.
        if (entityResponses.every(r => r.status === 'rejected')) return;

        const rawAlerts: RawAlert[] = [];

        for (const result of entityResponses) {
          if (result.status === 'rejected') continue;
          const { entity, response } = result.value;
          const errorsMap = detectErrors(response);
          const entityRef = stringifyEntityRef(entity);

          for (const [clusterName, detectedErrors] of errorsMap) {
            for (const err of detectedErrors) {
              if (err.sourceRef.kind !== 'Pod') continue;
              if (
                err.type === 'containers-restarting' &&
                err.occurrenceCount <= restartThreshold
              )
                continue;

              rawAlerts.push({
                alert: {
                  // Use proposedFix.errorType when available so two container-waiting
                  // errors on the same pod (e.g. CrashLoopBackOff + ImagePullBackOff
                  // in different containers) get distinct keys and independent dismiss state.
                  id: `${entityRef}:${err.sourceRef.name}:${clusterName}:${err.proposedFix?.errorType ?? err.type}`,
                  entityRef,
                  entityName: entity.metadata.name,
                  podName: err.sourceRef.name,
                  cluster: clusterName,
                  title: getAlertTitle(err),
                  detail: err.message,
                  kubernetesPath: `/catalog/${entity.metadata.namespace ?? 'default'}/${entity.kind.toLowerCase()}/${entity.metadata.name}/kubernetes`,
                  occurrenceCount: err.occurrenceCount,
                },
                severityRank: getSeverityRank(err),
              });
            }
          }
        }

        rawAlerts.sort((a, b) => {
          if (a.severityRank !== b.severityRank) return a.severityRank - b.severityRank;
          if (b.alert.occurrenceCount !== a.alert.occurrenceCount)
            return b.alert.occurrenceCount - a.alert.occurrenceCount;
          return a.alert.podName.localeCompare(b.alert.podName);
        });

        const alerts = rawAlerts.slice(0, MAX_ALERTS).map(r => r.alert);
        const overflowCount = Math.max(0, rawAlerts.length - MAX_ALERTS);

        if (!mountedRef.current) return;
        setState({ alerts, overflowCount });
      } catch {
        // Fail silently — preserve last known state; the next poll will retry.
      }
    };

    void poll();
    const id = setInterval(() => void poll(), POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [identityApi, catalogApi, kubernetesApi, kubernetesAuthProvidersApi, configApi, featureFlagsApi]);

  return state;
}
